import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  clearAlerts,
  setErrorAlert,
  setSuccessAlert,
} from '../../../store/alert/alertSlice';
import { postDataAPI } from '../../../utils/fetchData';
import { add_userInfo_thunk } from '../../../store/auth/authThunk';
import { LoginFun } from '../../../store/auth/authSlice';
import dizeeLogo from '../../../assets/images/dizee logo.svg';
import TypeWriter from '../../../components/OnBoard/Typewriter';
import AccessibilityWidget from '../../../components/AccessibilityWidget';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 350px;
  margin: 0 auto;
  padding-left: 35px;
  padding-right: 35px;
  background: #000;
  font-family: "Outfit", sans-serif;
`;

const FormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 40px;
  color: white;
`;

const ArtistGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  width: 100%;
  max-width: 300px;
`;

const ArtistCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #111111;

  &:hover {
    border-color: #FF2300;
    background: #1A1A1A;
  }

  &.selected {
    border-color: #FF2300;
    background: rgba(255, 35, 0, 0.05);
  }
`;

const ArtistAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(45deg, #6366f1, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 16px;
  overflow: hidden;
`;

const ArtistInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ArtistName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: white;
`;

const RoleInfo = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
`;

const ActionButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #FF2300;
    background: rgba(255, 35, 0, 0.1);
  }

  &.primary {
    background: #FF2300;
    border-color: #FF2300;
    font-weight: 600;
    
    &:hover:not(:disabled) {
      background: #e01f00;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TeamMemberDashboardButton = styled(ActionButton)`
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
  gap: 16px;
`;

const LogoImage = styled.img`
  height: 24px;
  width: auto;
`;

const FooterText = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  text-align: center;
  line-height: 1.4;
`;

// Role display names
const ROLE_DISPLAY_NAMES = {
  'manager': 'Manager',
  'agent': 'Agent',
  'label': 'Label',
  'publisher': 'Publisher',
  'business-manager': 'Business Manager'
};

const Step17 = ({ setStep, setUser, user }) => {
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [managedArtists, setManagedArtists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [isAutoCreating, setIsAutoCreating] = useState(false); // New state for auto-creation
  const { userData } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.body.style.touchAction = 'none';
    
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
      document.body.style.height = 'auto';
      document.body.style.touchAction = 'auto';
    };
  }, []);

  useEffect(() => {
    // For team members, automatically create account and go to dashboard
    // Skip the account selection UI entirely
    const autoCreateAccountAndNavigate = async () => {
      console.log('🚀 Auto-creating team member account and navigating to dashboard');
      
      const success = await createUserAccount();
      if (success) {
        // Navigate directly to team member dashboard
        setTimeout(() => {
          window.location.href = '/team-member-dashboard';
        }, 1000);
      }
    };

    // Automatically create account for team members
    if (user?.professionRole && ['manager', 'support'].includes(user.professionRole)) {
      setIsAutoCreating(true); // Set loading state immediately
      autoCreateAccountAndNavigate();
    } else {
      // Original logic for other user types (if any)
      if (user?.selectedArtists && user.selectedArtists.length > 0) {
        // Map the artist data structure from step16 to match step17 expectations
        const mappedArtists = user.selectedArtists.map(artist => ({
          _id: artist._id,
          name: artist.username || artist.email,
          email: artist.email,
          profilePicture: artist.avatar,
          followers: artist.followers || 0,
          tracks: artist.tracks || 0,
          domain: artist.domain,
          role: user.professionRole, // The team member's role
          joinedAt: new Date(),
          permissions: {},
          lastActiveAt: new Date()
        }));
        
        setManagedArtists(mappedArtists);
        
        // If there's only one artist, auto-select them
        if (mappedArtists.length === 1) {
          setSelectedArtist(mappedArtists[0]);
        }
      } else {
        // Fallback: fetch from API if no selected artists in user state
        fetchManagedArtists();
      }
    }
  }, [user?.selectedArtists, user?.professionRole]);

  const fetchManagedArtists = async () => {
    setLoading(true);
    try {
      const url = new URL(window.location.href);
      const emailParam = url.searchParams.get("email");
      const teamMemberEmail = emailParam || userData?.email;

      // During signup, user account doesn't exist yet, so we fetch pre-accepted invitations
      const response = await postDataAPI('team/invited-artists', {
        teamMemberEmail: teamMemberEmail
      });

      if (response.data?.status === 200) {
        // Filter for pre-accepted invitations (teams the user has joined during signup)
        const preAcceptedArtists = (response.data.data || []).filter(artist => 
          // This will include both pending and pre-accepted, but we'll show all available options
          true
        );
        
        setManagedArtists(preAcceptedArtists);
        
        // If there's only one artist, auto-select them
        if (preAcceptedArtists.length === 1) {
          setSelectedArtist(preAcceptedArtists[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching invited artists:', error);
      dispatch(setErrorAlert('Failed to load available artists'));
    } finally {
      setLoading(false);
    }
  };

  const handleAccessArtist = async () => {
    if (!selectedArtist) return;
    
    const success = await createUserAccount();
    if (success) {
      // Store the selected artist for the artist switcher
      localStorage.setItem('selectedArtist', JSON.stringify(selectedArtist));
      
      // Navigate to the main artist dashboard (acting as the artist)
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    }
  };

  const handleGoToTeamDashboard = async () => {
    const success = await createUserAccount();
    if (success) {
      // Navigate to team member dashboard
      setTimeout(() => {
        window.location.href = '/team-member-dashboard';
      }, 1000);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  const getRoleDisplayName = () => {
    return ROLE_DISPLAY_NAMES[user?.professionRole] || 'Team Member';
  };

  // Shared function to create user account
  const createUserAccount = async () => {
    setCreatingAccount(true);
    dispatch(clearAlerts());
    
    // Create a copy of the user object to avoid modifying the original directly
    const userToSubmit = {...user};
    
    // Add default cover if it doesn't exist
    if (!userToSubmit.cover) {
      userToSubmit.cover = null;
    }
    
    try {
      // Get email from URL parameters
      const url = new URL(window.location.href);
      const emailParam = url.searchParams.get('email');
      const userParam = url.searchParams.get('user');
      
      if (userParam) {
        try {
          const parsedUserParam = JSON.parse(userParam);
          userToSubmit.email = parsedUserParam.email;
        } catch (e) {
          console.error('Failed to parse user parameter:', e);
          userToSubmit.email = emailParam;
        }
      } else {
        userToSubmit.email = emailParam;
      }
      
      // Validate required fields
      if (!userToSubmit.email) {
        dispatch(setErrorAlert('Email is required'));
        setCreatingAccount(false);
        return null;
      }

      // Validate social URLs if they exist
      if (userToSubmit.social) {
        Object.keys(userToSubmit.social).forEach(platform => {
          const url = userToSubmit.social[platform];
          if (url && typeof url === 'string' && !url.includes('://')) {
            userToSubmit.social[platform] = `https://${url}`;
          }
        });
      }
      
      console.log('Creating user account:', userToSubmit);
      
      // Create the user account
      const response = await dispatch(add_userInfo_thunk(userToSubmit));
      console.log('User creation response:', response);
      
      if (response?.payload?.status === 200) {
        dispatch(LoginFun(true));
        dispatch(setSuccessAlert('Account created successfully!'));
        return true;
      } else {
        const errorMsg = response?.payload?.data?.message || 'Failed to create account';
        dispatch(setErrorAlert(errorMsg));
        setCreatingAccount(false);
        return false;
      }
    } catch (error) {
      console.error('Error creating account:', error);
      dispatch(setErrorAlert('An error occurred while creating your account'));
      setCreatingAccount(false);
      return false;
    }
  };

  // Removed loading state display

  // Show loading screen for team members during auto-creation
  if (isAutoCreating) {
    return (
      <Container>
        <FormSection>
          <div style={{ textAlign: 'left', width: '280px' }}>
            <TypeWriter text="Creating your account..." speed={45} />
            <div className="text-white text-[12px] leading-[1.4] mt-[20px] w-[280px]">
              Setting up your team member dashboard...
            </div>
          </div>
        </FormSection>
        <AccessibilityWidget />
    </Container>
    );
  }

  return (
    <Container>
      <FormSection>
        <TypeWriter text="Which account would you like to access?" speed={45} />
        
        {managedArtists.length > 0 ? (
          <ArtistGrid>
            {managedArtists.map((artist) => (
              <ArtistCard
                key={artist._id}
                className={selectedArtist?._id === artist._id ? 'selected' : ''}
                onClick={() => setSelectedArtist(artist)}
              >
                <ArtistAvatar>
                  {artist.profilePicture ? (
                    <img 
                      src={artist.profilePicture} 
                      alt={artist.name}
                      style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : (
                    getInitials(artist.name)
                  )}
                </ArtistAvatar>
                <ArtistInfo>
                  <ArtistName>{artist.name}</ArtistName>
                  <RoleInfo>
                    {artist.followers || 0} followers • Managing as {ROLE_DISPLAY_NAMES[artist.role] || artist.role}
                  </RoleInfo>
                </ArtistInfo>
              </ArtistCard>
            ))}
          </ArtistGrid>
        ) : (
          <div style={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: 'center' }}>
            No artist accounts available to manage yet.
          </div>
        )}

        <ButtonContainer>
          {managedArtists.length > 0 && (
            <ActionButton
              className="primary"
              onClick={handleAccessArtist}
              disabled={!selectedArtist || creatingAccount}
            >
              {creatingAccount ? 'Creating Account...' : 'Access Artist Account'}
            </ActionButton>
          )}
          
          <TeamMemberDashboardButton 
            onClick={handleGoToTeamDashboard}
            disabled={creatingAccount}
          >
            {creatingAccount ? 'Creating Account...' : `Go to Team Hub`}
          </TeamMemberDashboardButton>
          
          <ActionButton 
            onClick={() => setStep(16)}
            disabled={creatingAccount}
          >
            Go back
          </ActionButton>
        </ButtonContainer>
      </FormSection>
      
      <Footer>
        <LogoImage src={dizeeLogo} alt="DIZEE" />
        <FooterText>
          {selectedArtist ? (
            `You're about to access ${selectedArtist.name}'s account as their ${getRoleDisplayName().toLowerCase()}. You'll be able to manage their content and settings as if you were them.`
          ) : managedArtists.length > 0 ? (
            `Select an artist account to manage, or go to your ${getRoleDisplayName().toLowerCase()} dashboard to see all available options.`
          ) : (
            `Welcome! You can access your ${getRoleDisplayName().toLowerCase()} dashboard to request access to artist accounts.`
          )}
        </FooterText>
      </Footer>
      <AccessibilityWidget />
    </Container>
  );
};

export default Step17; 